import React, { Component, Fragment } from "react";
import { debounce, has, isFunction } from "lodash";
import { inject, observer } from "mobx-react";

import { Button, Radio, message } from "antd";
import LoadingOverlay from "react-loading-overlay";
import { PulseLoader } from "react-spinners";

import { ErrorOutlinedIcon, InfoIcon } from "../../../icons";
import { mypageHelper } from "../../../../helpers";
import { api } from "../../../../services";
import rollbar from "../../../../services/rollbar";
import K from "../../../../constants";

import leonardoStore from "../../../../stores/leonardoStore";

import PrivacyAgreements from "../PrivacyAgreements/PrivacyAgreements";
import SlimpayIframe from "./SlimpayForm";

import "./PaymentForm.css";

const CreditCardFriendlyReminder = () => {
  return (
    <div className="FriendlyReminder">
      <InfoIcon
        width={14}
        height={14}
        style={{ color: "var(--action-primary-color)", marginRight: 10 }}
      />
      Per determinate carte di credito è previsto il pagamento di 0€ al fine di
      verificare la validità della carta. Non appena la verifica verrà
      completata con successo, si procederà al pagamento della somma dovuta."
    </div>
  );
};

const radioStyle = {
  display: "block",
  height: "30px",
  lineHeight: "30px",
};

@inject("translationsStore", "mypageStore", "productsStore")
@observer
export default class PaymentView extends Component {
  zuoraDOMID = "zuora_payment";

  /* -------------------------------------------------------- */
  /* ----------------- Constructor and Lifecycle ------------ */
  /* --- Set up the component state and lifecycle methods --- */
  /* -------------------------------------------------------- */

  constructor(props) {
    super(props);
    this.state = {
      isSubmitting: false,
      selectedPaymentMethod: "sepa",
      loadingToken: false,
      renderingPaymentFrame: false,
      hideSubmitPaymentButton: false,
      lastPaymentErrorMessage: undefined,
      addOnServiceAgreements: {
        air_club_profiling: undefined,
        air_club_marketing: undefined,
        air_club_third_parties: undefined,
      },
    };

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // zuora hosted page payment is inside a frame
        if (
          mutation.target.tagName.toUpperCase() === "IFRAME" &&
          mutation.type === "attributes" &&
          mutation.attributeName === "height"
        ) {
          this.setState({ renderingPaymentFrame: false });
        }
      });
    });
  }

  // Called at the first render
  // This is where we set up the mutation observer to watch for changes in the DOM
  componentDidMount() {
    const { selectedPaymentMethod } = this.state;

    window.zuoraHostedSubmitFailed = this.zuoraHostedSubmitFailed;
    window.zuoraHostedSubmitSucceed = this.zuoraHostedSubmitSucceed;
    window.zuoraHostedSubmitProcessing = this.zuoraHostedSubmitProcessing;
    window.Z.setEventHandler("onCaptchaStateChange", this.removeModalLayer);

    window.zuoraHostedSubmitSucceed = (...args) => {
      this.zuoraHostedSubmitSucceed(...args);
    };

    this.setState(
      {
        checkedAgreedGeneralConditions: false,
        checkedAgreedRestrictiveClauses: false,
      },
      () => {
        switch (selectedPaymentMethod) {
          case "creditcard":
            this.loadPayment();
        }
      }
    );
  }

  // Called after each update (e.g., after a state change)
  // This is where we set up the mutation observer to watch for changes in the DOM
  componentDidUpdate = () => {
    const zuoraDOM = document.getElementById(this.zuoraDOMID);
    if (zuoraDOM !== null) {
      this.mutationObserver.observe(zuoraDOM, {
        attributes: true,
        subtree: true,
      });
    }
  };

  // Called after the component is removed from the DOM
  // This is where we clean up the mutation observer
  componentWillUnmount = () => {
    window.zuoraHostedSubmitFailed = undefined;
    window.zuoraHostedSubmitSucceed = undefined;
    window.zuoraHostedSubmitProcessing = undefined;

    this.mutationObserver.disconnect();
  };

  // Called when there is an error in the payment
  // This is where we show an error message
  showErrorMessage = () => {
    message.error("Oops! C'è stato un problema");
  };

  // Called to load the credit card
  // This is where we fetch the token from the API
  // and set the parameters for the Zuora Hosted Page
  loadCreditCard = async () => {
    const { translationsStore } = this.props;
    this.setState({ loadingToken: true, renderingPaymentFrame: true });
    const pageId = process.env.REACT_APP_ZUORA_HOSTED_PAGE_ID;
    const result = await api("getZuoraHostedPageToken", pageId).finally(() =>
      this.setState({ loadingToken: false })
    );
    const locale =
      translationsStore.translate("common.localeISO") !== "common.localeISO"
        ? translationsStore.translate("common.localeISO")
        : "it_IT";

    return {
      id: pageId,
      signature: result.signature,
      token: result.token,
      tenantId: result.tenantId,
      key: result.key,
      field_accountId: leonardoStore.user.zuora_account_id,
      style: "inline",
      submitEnabled: "false",
      locale: locale,
      param_supportedTypes: "Visa,MasterCard",
      url: process.env.REACT_APP_ZUORA_HOSTED_PAGE_URL,
      paymentGateway: process.env.REACT_APP_ZUORA_PAYMENT_GATEWAY,
    };
  };

  // Called to load the payment with zuora
  // This is where we render the Zuora Hosted Page
  // and pass the parameters to it
  loadPayment = () => {
    const loadMe = async () => {
      const params = await this.loadCreditCard();
      const callback = async (response) => {
        if (!response.success) {
          this.showErrorMessage();
          return;
        }
      };

      // render the Zuora Hosted Page
      window.Z.renderWithErrorHandler(
        params,
        { creditCardCountry: "ITA" },
        callback,
        this.zuoraClientErrorMessageCallback,
        null,
        null,
        this.removeModalLayer
      );
    };
    loadMe();
  };

  // Called when the payment is submitted
  // Update the privacy policy and the marketing consent
  // and submit the payment (for credit card)
  onPaymentSubmit = async () => {
    const { selectedPaymentMethod } = this.state;

    if (leonardoStore.isAssistance) {
      const {
        checkedAgreedGeneralConditions,
        checkedAgreedRestrictiveClauses,
      } = this.state;
      const {
        addOnServiceAgreements: {
          airclub_general,
          airclub_general_articles,
          air_club_profiling,
          air_club_marketing,
          air_club_third_parties,
        },
      } = this.state;

      await this.props.mypageStore.updateAirClubMarketingConsent(
        checkedAgreedGeneralConditions,
        air_club_profiling,
        air_club_marketing,
        air_club_third_parties
      );
    }

    this.setState(
      {
        isSubmitting: true,
        renderingPaymentFrame: true,
        lastPaymentErrorMessage: undefined,
      },
      () => {
        switch (selectedPaymentMethod) {
          case "creditcard":
            window.Z.submit();
            break;
          case "sepa":
            this.setState({
              hasSubmittedSepaAgreement: true,
              isSubmitting: false,
            });
            break;
        }
      }
    );
  };

  // Debounce function used to prevent multiple calls
  // Allow only one call every 1 second
  debouncedClearSubmitting = debounce((cb) => {
    this.setState({ isSubmitting: false }, () => {
      isFunction(cb) && cb();
    });
  }, 1000);

  // Handle the removal of the modal layer
  // This is where we hide the submit payment button
  removeModalLayer = () => {
    this.setState({
      isSubmitting: false,
      renderingPaymentFrame: false,
      hideSubmitPaymentButton: true,
    });
  };

  /* ------------------------------------------------------- */
  /* ------------------- Zuora Callbacks ------------------- */
  /* ----- Handle the callbacks from Zuora Hosted Page ----- */
  /* ------------------------------------------------------- */

  // Callback function when there is a client error
  zuoraClientErrorMessageCallback = (key, code, message) => {
    this.debouncedClearSubmitting();
    window.Z.sendErrorMessageToHpm(key, message);
  };

  // Callback function when there is a server error
  zuoraServerErrorMessageCallback = (callbackResponse) => {
    this.setState({
      isSubmitting: false,
      lastPaymentErrorMessage: callbackResponse.errorMessage,
    });
  };

  // Callback function when the hosted zuora page fails
  // This is where we show an error message
  // and reload the payment iframe
  zuoraHostedSubmitFailed = (callbackResponse) => {
    const { mypageStore, onPaymentResult } = this.props;

    if (
      !(
        callbackResponse &&
        callbackResponse.errorCode &&
        callbackResponse.errorCode.includes("ThreeDs2_Authentication_Exception")
      )
    ) {
      rollbar.error(
        `Error zuora hosted page submit for user with uuid ${
          mypageStore.remoteUserData.uuid
        }, callbackResponse ${JSON.stringify(callbackResponse)}`
      );
    }

    const loadMeWhenSubmitFailed = async () => {
      const params = await this.loadCreditCard();

      const callback = async (response) => {
        if (!response.success) {
          this.showErrorMessage();
          return;
        }
      };

      window.Z.renderWithErrorHandler(
        params,
        { creditCardCountry: "ITA" },
        callback,
        this.zuoraClientErrorMessageCallback,
        null,
        null,
        this.removeModalLayer
      );
      window.Z.runAfterRender(() => {
        this.zuoraServerErrorMessageCallback(callbackResponse);

        if (onPaymentResult) {
          onPaymentResult(false); // ERROR
        }
      });
    };
    loadMeWhenSubmitFailed();
  };

  // Callback function when the hosted zuora page succeeds
  // This is where we exit the payment iframe
  // and subscribe the user to the product via API
  zuoraHostedSubmitSucceed = async (callbackResponse) => {
    const { productsStore, onPaymentResult } = this.props;

    try {

      await productsStore.subscribeToProduct(
        "Carta di credito",
        callbackResponse.refId
      );

      if (onPaymentResult) {
        onPaymentResult(true); // SUCCESS
      }
    } catch (error) {
      this.showErrorMessage();

      if (onPaymentResult) {
        onPaymentResult(false); // ERROR
      }
    }
  };

  //Callback function when the hosted zuora page is processing
  // Show an error message if there is an error
  zuoraHostedSubmitProcessing = (callbackResponse) => {
    const { onPaymentResult } = this.props;

    if (callbackResponse.errorCode) {
      this.showErrorMessage();
      this.setState({ hideSubmitPaymentButton: false });

      if (onPaymentResult) {
        onPaymentResult(false); // ERROR
      }
    } else {
      this.setState({
        renderingPaymentFrame: true,
        hideSubmitPaymentButton: true,
      });
    }
  };

  /* ------------------------------------------------------- */
  /* -------------------- Event Handlers ------------------- */
  /* ------------------------------------------------------- */

  // Handle the change of the payment method
  // This is where we load the payment iframe when the user selects
  // the credit card payment method
  onPaymentMethodChange = (e) => {
    const selectedPaymentMethod = e.target.value;
    this.setState({ selectedPaymentMethod }, () => {
      switch (selectedPaymentMethod) {
        case "creditcard":
          this.loadPayment();
      }
    });
  };

  /* -------------------------------------------------------- */
  /* ------------------------ Render ------------------------ */
  /* ----------- Render the component UI elements ----------- */
  /* -------------------------------------------------------- */

  render() {
    const {
      checkedAgreedGeneralConditions,
      checkedAgreedRestrictiveClauses,
      visibleStandardFormContract,
      lastPaymentErrorMessage,
      selectedPaymentMethod,
    } = this.state;

    const {
      addOnServiceAgreements: {
        airclub_general,
        airclub_general_articles,
        air_club_profiling,
        air_club_marketing,
        air_club_third_parties,
      },
    } = this.state;

    const { translationsStore } = this.props;

    const isCheckedConditionsRequiredForSelectedProduct =
      leonardoStore.isProductMovein ||
      leonardoStore.isProductRenew ||
      leonardoStore.isAssistance ||
      leonardoStore.isMyAppAddon;
    let validAgreements =
      checkedAgreedGeneralConditions && checkedAgreedRestrictiveClauses;
    if (leonardoStore.user.movein && leonardoStore.isAssistance) {
      validAgreements =
        validAgreements &&
        air_club_profiling !== undefined &&
        air_club_marketing !== undefined &&
        air_club_third_parties !== undefined;
    }
    const disabledPaymentSubmit =
      this.state.isSubmitting ||
      !(validAgreements || !isCheckedConditionsRequiredForSelectedProduct);

    return (
      <LoadingOverlay
        active={this.state.renderingPaymentFrame || this.state.isSubmitting}
        spinner={
          <PulseLoader
            sizeUnit={"px"}
            size={20}
            color={"var(--action-primary-color)"}
            loading={true}
          />
        }
        styles={{
          overlay: (base) => ({
            ...base,
            background: "var(--grey-light-color-50)",
          }),
        }}
        className="PaymentMethodContainer"
      >
        <div className="paymentMethodHeader">
          Seleziona il metodo di pagamento
        </div>
        <div className="PaymentPane">
          <Radio.Group
            onChange={this.onPaymentMethodChange}
            value={this.state.selectedPaymentMethod}
          >
            <Radio style={radioStyle} value={"creditcard"}>
              {mypageHelper.paymentIcon("creditcard")}{" "}
              {translationsStore.translate(
                `common.paymentMethods.creditcard.label`
              )}
            </Radio>
            <Radio style={radioStyle} value={"sepa"}>
              {mypageHelper.paymentIcon("sepa")}{" "}
              {translationsStore.translate(`common.paymentMethods.sepa.label`)}
            </Radio>
          </Radio.Group>
        </div>

        {lastPaymentErrorMessage && (
          <div className="PaymentErrorMessage PaymentPane">
            <ErrorOutlinedIcon />
            {"Errore durante la procedura di pagamento"}
            <i>{lastPaymentErrorMessage}</i>
          </div>
        )}

        <Fragment>
          {(() => {
            switch (selectedPaymentMethod) {
              case "creditcard":
                return (
                  <div
                    id={this.zuoraDOMID}
                    className="ZuoraPayment PaymentPane"
                  />
                );
              case "sepa":
                return (
                  this.state.hasSubmittedSepaAgreement && (
                    <SlimpayIframe
                      hasSubmittedSepaAgreement={
                        this.state.hasSubmittedSepaAgreement
                      }
                      onIframeLoaded={() => {
                        this.setState({ renderingPaymentFrame: false });
                      }}
                      onPaymentResult={(success) => {
                        this.props.onPaymentResult(success);
                      }}
                    />
                  )
                );
              default:
                return null;
            }
          })()}

          {selectedPaymentMethod === "creditcard" && (
            <CreditCardFriendlyReminder />
          )}

          <Fragment>
            {leonardoStore.isProductMovein &&
              !this.state.hasSubmittedSepaAgreement && (
                <PrivacyAgreements
                  checkedAgreedGeneralConditions={
                    checkedAgreedGeneralConditions
                  }
                  checkedAgreedRestrictiveClauses={
                    checkedAgreedRestrictiveClauses
                  }
                  onChangeAgreedGeneralConditions={(checked) => {
                    this.setState({ checkedAgreedGeneralConditions: checked });
                  }}
                  onChangeAgreedRestrictiveClauses={(checked) => {
                    this.setState({ checkedAgreedRestrictiveClauses: checked });
                  }}
                  isMoVeInRenewal={leonardoStore.isProductMoveinRenewal}
                  isAssistance={leonardoStore.isAssistance}
                />
              )}

            {leonardoStore.isProductRenew &&
              !this.state.hasSubmittedSepaAgreement && (
                <PrivacyAgreements
                  isAssistance={leonardoStore.isAssistance}
                  checkedAgreedGeneralConditions={
                    checkedAgreedGeneralConditions
                  }
                  checkedAgreedRestrictiveClauses={
                    checkedAgreedRestrictiveClauses
                  }
                  onChangeAgreedGeneralConditions={(checked) => {
                    this.setState({ checkedAgreedGeneralConditions: checked });
                  }}
                  onChangeAgreedRestrictiveClauses={(checked) => {
                    this.setState({ checkedAgreedRestrictiveClauses: checked });
                  }}
                  isRenewProduct={leonardoStore.isProductRenew}
                />
              )}

            {(leonardoStore.isAssistance || leonardoStore.isMyAppAddon) &&
              !this.state.hasSubmittedSepaAgreement && (
                <PrivacyAgreements
                  isAssistance={leonardoStore.isAssistance}
                  isMyApp={leonardoStore.isMyAppAddon}
                  checkedAgreedGeneralConditions={
                    checkedAgreedGeneralConditions
                  }
                  checkedAgreedRestrictiveClauses={
                    checkedAgreedRestrictiveClauses
                  }
                  onChangeAgreedGeneralConditions={(checked) => {
                    this.setState({ checkedAgreedGeneralConditions: checked });
                  }}
                  onChangeAgreedRestrictiveClauses={(checked) => {
                    this.setState({ checkedAgreedRestrictiveClauses: checked });
                  }}
                  hideSubTitle={true}
                  generalConditionsDocumentSource={K.POLICY_BASE_URL.concat(
                    "AIR+-+Condizioni+generali+Servizi+aggiuntivi.pdf"
                  )}
                  labelRestrictiveConditions={translationsStore.translate(
                    "installerView.sectionAgreements.labelRestrictiveClausesAdditionalProduct"
                  )}
                  labelGeneralConditionPrefix={translationsStore.translate(
                    "installerView.sectionAgreements.labelAdditionalGeneralConditionsPrefix"
                  )}
                  labelGeneralConditionClickable={translationsStore.translate(
                    "installerView.sectionAgreements.labelGeneralConditions"
                  )}
                  labelGeneralConditionSuffix={translationsStore.translate(
                    "installerView.sectionAgreements.labelAdditionalGeneralConditionsSuffix"
                  )}
                  {...(leonardoStore.remoteUserIsFromMoVeInProgram
                    ? {
                        addOnServiceAgreements: this.state
                          .addOnServiceAgreements,
                        onAddOnServiceAgreementsChange: (
                          addOnServiceAgreements
                        ) => this.setState({ addOnServiceAgreements }),
                      }
                    : {})}
                />
              )}

            {!this.state.hideSubmitPaymentButton &&
              !this.state.hasSubmittedSepaAgreement && (
                <Button
                  className="PaymentProceedButton"
                  type="primary"
                  style={{ marginTop: 40, height: 40, fontSize: 16 }}
                  disabled={disabledPaymentSubmit}
                  onClick={this.onPaymentSubmit}
                >
                  {translationsStore.translate(
                    "installerView.labelPaymentProceed"
                  )}
                </Button>
              )}
          </Fragment>
        </Fragment>
      </LoadingOverlay>
    );
  }
}
